/**
 * Touch detail's rows as touch-driver input (touchDrivers.ts): the touches people MADE,
 * by touch date — Touch detail's default basis, and what the prototype's export was.
 *
 * Every column comes from the touch file (`build_bundle.touch_detail`): `e` who touched,
 * `f` + `v` the claim, `d` the date, `sd` / `ad` the raw status and action, `note`. The
 * note is read for the blank-note and duplicate tests and never shown.
 */
import type { TouchDetailData } from './touchDetail'
import { selectTouches } from './touchRows'
import { BLANK_NOTE_MIN, type DriverRows } from './touchDrivers'

export interface DriverSource {
  /** The rows, plus what each id means. */
  rows: DriverRows & { counted: Uint8Array; month: Int32Array }
  /** File row behind each driver row. */
  fileRow: Int32Array
  /** Person id → name (the toucher, Touch detail's `e`). */
  people: string[]
  /** Trimmed status / action id → text. */
  statuses: string[]
  actions: string[]
  /** Claim id → facility and visit. */
  claims: { f: string; v: string }[]
  /** Month id → "YYYY-MM". */
  months: string[]
}

export interface DriverSelection {
  /** The top bar's months, "YYYY-MM". */
  months: readonly string[]
  /** Months before them read only to find repeats; [] for none. */
  lookBack: readonly string[]
  /** Whose touches. */
  people: ReadonlySet<string>
  /** Touch detail's other narrowing — the Overview slice and facility group columns. */
  picks: readonly { col: readonly number[]; want: ReadonlySet<number> }[]
}

/** "2026-03" → "2026-02". */
export function previousMonth(m: string): string {
  const y = Number(m.slice(0, 4))
  const mo = Number(m.slice(5, 7))
  return mo === 1 ? `${y - 1}-12` : `${y}-${String(mo - 1).padStart(2, '0')}`
}

const DAY = 86_400_000

export function driverSource(data: TouchDetailData, sel: DriverSelection): DriverSource {
  const { dict, cols } = data
  const all = new Set([...sel.lookBack, ...sel.months])
  const inRange = new Set(sel.months)
  const picked = selectTouches(data, {
    basis: 'touch',
    months: all,
    inScope: sel.people,
    inAnyStatus: sel.people,
    team: null,
    picks: sel.picks,
    hit: null,
  }).rows

  // Ids handed out in first-seen order; the dictionaries only carry what is used.
  const ids = () => {
    const map = new Map<string, number>()
    const list: string[] = []
    return {
      list,
      of(s: string) {
        let i = map.get(s)
        if (i === undefined) map.set(s, (i = list.push(s) - 1))
        return i
      },
    }
  }
  const person = ids()
  const status = ids()
  const action = ids()
  const month = ids()
  const claimIds = new Map<string, number>()
  const claims: { f: string; v: string }[] = []
  // Per dictionary entry, once.
  const statusOf = dict.sd.map((s) => status.of(s.trim()))
  const actionOf = dict.ad.map((s) => action.of(s.trim()))
  const dayOf = dict.d.map((d) => (d ? Math.round(Date.parse(`${d.slice(0, 10)}T00:00:00Z`) / DAY) : 0))

  const n = picked.length
  const out = {
    n,
    person: new Int32Array(n),
    claim: new Int32Array(n),
    status: new Int32Array(n),
    action: new Int32Array(n),
    day: new Int32Array(n),
    counted: new Uint8Array(n),
    month: new Int32Array(n),
    blank: new Uint8Array(n),
  }
  const fileRow = Int32Array.from(picked)
  for (let k = 0; k < n; k++) {
    const i = picked[k]!
    out.person[k] = person.of(dict.e[cols.e[i]!]!)
    const fk = `${cols.f[i]}\u0001${cols.v[i]}`
    let c = claimIds.get(fk)
    if (c === undefined) {
      c = claims.push({ f: dict.f[cols.f[i]!]!, v: cols.v[i]! }) - 1
      claimIds.set(fk, c)
    }
    out.claim[k] = c
    out.status[k] = statusOf[cols.sd[i]!]!
    out.action[k] = actionOf[cols.ad[i]!]!
    out.day[k] = dayOf[cols.d[i]!]!
    const m = dict.d[cols.d[i]!]!.slice(0, 7)
    out.month[k] = month.of(m)
    out.counted[k] = inRange.has(m) ? 1 : 0
    out.blank[k] = (cols.note[i] ?? '').trim().length < BLANK_NOTE_MIN ? 1 : 0
  }
  // Identical rows: who, claim, date, the raw status and action, the trimmed note.
  const sig = (k: number) => {
    const i = fileRow[k]!
    return `${cols.e[i]}|${cols.f[i]}|${cols.v[i]}|${cols.d[i]}|${cols.sd[i]}|${cols.ad[i]}|${(cols.note[i] ?? '').trim()}`
  }
  return {
    rows: { ...out, sig },
    fileRow,
    people: person.list,
    statuses: status.list,
    actions: action.list,
    claims,
    months: month.list,
  }
}
